import React, { ReactNode, useEffect, useState } from 'react';
import {
  Breadcrumbs,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GroupsViewCreator } from '../../view-creator/GroupsViewCreator';
import groupsViewModel, { GroupsView } from '../../views/GroupsView';
import { GroupView } from '../../views/GroupView';
import { palette } from '../../palette';

export const Groups: React.FC = () => {
  const navigate = useNavigate();
  const onGroupClick = (groupId: number) => {
    navigate(`/dashboard/${groupId}`);
  };
  const [view, setView] = useState<GroupsView>(groupsViewModel.Empty);
  const [reload] = useState<boolean>(false);
  useEffect(() => {
    new GroupsViewCreator().CreateView().then((view) => {
      setView(view);
    });
  }, [reload]);

  useEffect(() => {
    // If the user has only one group, redirect to the dashboard of that group
    if (view.groups.length === 1) {
      navigate(`/dashboard/${view.groups[0].id}`);
    }
  }, [view.groups]);

  const currentTeams = view.groups.filter((group) => !group.finalized);
  const pastTeams = view.groups.filter((group) => group.finalized);

  const renderCard = (group: GroupView, isPast: boolean): ReactNode => (
    <Card
      key={group.id}
      variant={'elevation'}
      sx={{
        minWidth: '245px',
        maxWidth: '245px',
        cursor: 'pointer',
        backgroundColor: '#FFF',
        borderRadius: '16px',
        ...(isPast && { color: '#808080', opacity: 0.8 }),
      }}
      onClick={() => {
        onGroupClick(group.id);
      }}
    >
      <CardActionArea focusRipple sx={{ height: '100%', padding: 1 }}>
        <Typography gutterBottom variant="h5" sx={{ color: isPast ? '#808080' : palette.primary.main }} component="div">
          {group.name}
        </Typography>
        <CardContent>
          <Typography
            variant="body1"
            sx={{
              color: isPast ? '#808080' : 'text.secondary',
            }}
          >
            {group.shortDescription}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  const renderSkeletonCard = (): ReactNode => (
    <Card key={-1} variant="elevation" sx={{ minWidth: '245px' }}>
      <CardActionArea sx={{ height: '100%', padding: 1 }}>
        <Typography gutterBottom variant="h5" component="div">
          <Skeleton variant="text" width="80%" />
        </Typography>
        <Skeleton variant="rectangular" width="100%" height={140} />
        <CardContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="80%" />
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  const isLoading = view.groups.length === 0;

  return (
    <Container sx={{ backgroundColor: '#F4F4F4' }}>
      <Breadcrumbs aria-label="breadcrumb" separator={'>'}>
        <Link underline="hover" color="inherit" href="/">
          Home
        </Link>

        <Typography
          sx={{
            color: 'text.primary',
          }}
        >
          My Teams
        </Typography>
      </Breadcrumbs>

      {/* My Current Teams */}
      <Typography gutterBottom variant="h6" component="div">
        My Current Teams
      </Typography>
      <Stack
        direction="row"
        spacing={0}
        sx={{
          justifyContent: { xs: 'center', sm: 'flex-start' },
          flexShrink: 0,
          alignItems: 'stretch',
          padding: 1,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {isLoading ? renderSkeletonCard() : currentTeams.map((group) => renderCard(group, false))}
      </Stack>

      {/* Divider between current and past teams */}
      <hr style={{ marginTop: '60px', marginBottom: '60px', border: '1px solid #ccc' }} />

      {/* My Past Teams */}
      <Typography gutterBottom variant="h6" component="div" sx={{ color: '#808080', opacity: 0.8 }}>
        My Past Teams
      </Typography>
      <Stack
        direction="row"
        spacing={0}
        sx={{
          justifyContent: { xs: 'center', sm: 'flex-start' },
          flexShrink: 0,
          alignItems: 'stretch',
          padding: 1,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {isLoading ? renderSkeletonCard() : pastTeams.map((group) => renderCard(group, true))}
      </Stack>
    </Container>
  );
};
